import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { KMU_TEMPLATE } from "@shared/kontenplanTemplates";

// Leaf-level accounts useful for transaction categorization (level >= 3)
const ACCOUNTS = KMU_TEMPLATE.filter(
  a => a.level >= 3 && a.code !== "1000" // exclude duplicate root
);

interface AccountCodeComboboxProps {
  value: string;
  onSelect: (code: string, name: string) => void;
  className?: string;
}

export function AccountCodeCombobox({
  value,
  onSelect,
  className,
}: AccountCodeComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const q = search.toLowerCase();
  const filtered = q
    ? ACCOUNTS.filter(
        a =>
          a.code.includes(q) ||
          a.nameGerman.toLowerCase().includes(q) ||
          a.name.toLowerCase().includes(q)
      )
    : ACCOUNTS;

  const selected = ACCOUNTS.find(a => a.code === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal min-h-[48px] text-left",
            !selected && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate">
            {selected
              ? `${selected.code} — ${selected.nameGerman}`
              : "Search account code…"}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Code or German description…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No account found.</CommandEmpty>
            <CommandGroup>
              {filtered.slice(0, 60).map(account => (
                <CommandItem
                  key={account.code}
                  value={account.code}
                  onSelect={() => {
                    onSelect(account.code, account.nameGerman);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0",
                      value === account.code ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="font-mono text-xs mr-3 text-muted-foreground w-12 shrink-0">
                    {account.code}
                  </span>
                  <span className="truncate">{account.nameGerman}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
